<template>
  <section class="version-preview-list h-full flex flex-col" :style="themeColorVars">
    <header class="version-preview-list__header">
      <div class="min-w-0">
        <h2 class="version-preview-list__title">版本历史</h2>
      </div>
    </header>

    <div ref="listBodyRef" class="version-preview-list__body">
      <div
        v-if="loading"
        class="version-preview-list__loading"
        role="status"
        aria-live="polite"
      >
        <n-spin size="medium" />
        <span class="version-preview-list__loading-text">正在加载版本记录...</span>
      </div>

      <n-empty
        v-else-if="records.length === 0"
        description="暂无版本记录"
        size="small"
        class="version-preview-list__empty"
      />

      <template v-else>
        <button
          v-for="record in records"
          :key="record.id"
          class="version-preview-list__item"
          :class="{
            'version-preview-list__item--selected': record.id === selectedVersionId,
            'version-preview-list__item--switching': isSwitchingVersion(record.id),
          }"
          type="button"
          :data-version-id="record.id"
          :aria-busy="isSwitchingVersion(record.id)"
          @click="emit('select', record.id)"
        >
          <span class="version-preview-list__title-row">
            <span class="version-preview-list__title-marker" aria-hidden="true"></span>
            <n-tooltip
              trigger="hover"
              placement="top-start"
              :show-arrow="false"
              :theme-overrides="tooltipThemeOverrides"
              :content-style="tooltipContentStyle"
            >
              <template #trigger>
                <span class="version-preview-list__item-title">{{ record.title }}</span>
              </template>
              <span>{{ record.title }}</span>
            </n-tooltip>
            <n-tag
              v-if="record.isAdoptedVersion"
              class="version-preview-list__adopted-tag"
              size="small"
              type="info"
              round
            >
              采纳版本
            </n-tag>
            <n-spin
              v-if="isSwitchingVersion(record.id)"
              class="version-preview-list__switching-spin"
              size="small"
            />
          </span>
          <span class="version-preview-list__branch-row">
            <n-tooltip
              trigger="hover"
              placement="top-start"
              :show-arrow="false"
              :theme-overrides="tooltipThemeOverrides"
              :content-style="tooltipContentStyle"
            >
              <template #trigger>
                <span class="version-preview-list__branch-group">
                  <svg
                    class="version-preview-list__branch-icon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      d="M6 3v12m0 0a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm12-6a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm0 0a6 6 0 0 1-6 6H9"
                    />
                  </svg>
                  <span class="version-preview-list__branch">{{ record.branchName }}</span>
                </span>
              </template>
              <span>{{ record.branchName }}</span>
            </n-tooltip>
            <n-tooltip
              trigger="hover"
              placement="top"
              :show-arrow="false"
              :theme-overrides="tooltipThemeOverrides"
              :content-style="tooltipContentStyle"
            >
              <template #trigger>
                <span class="version-preview-list__author">{{ record.authorName }}</span>
              </template>
              <span>{{ record.authorName }}</span>
            </n-tooltip>
          </span>
          <span class="version-preview-list__time">
            {{ formatDate(record.committedAt) }}
          </span>
        </button>
      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import type { CSSProperties } from 'vue';
import { NEmpty, NSpin, NTag, NTooltip } from 'naive-ui';
import { useEditorTooltipTheme } from '@/hooks/useEditorTooltipTheme';
import type { VersionPreviewRecord } from '@/services/version-preview';
import { useGlobalConfigStore } from '@/stores';

interface VersionPreviewListPanelProps {
  /** 版本记录列表。 */
  records: readonly VersionPreviewRecord[];
  /** 当前选中的版本 ID。 */
  selectedVersionId: string | null;
  /** 初始加载完成后需要滚动到的版本 ID；缺省时不执行初始滚动。 */
  initialScrollVersionId?: string | null;
  /** 是否正在加载版本列表。 */
  loading: boolean;
  /** 当前正在切换模型的版本 ID。 */
  switchingVersionId: string | null;
}

const props = defineProps<VersionPreviewListPanelProps>();

const emit = defineEmits<{
  /** 选择版本。 */
  select: [versionId: string];
}>();

const globalConfigStore = useGlobalConfigStore();
const listBodyRef = ref<HTMLElement | null>(null);
const consumedInitialScrollVersionId = ref<string | null>(null);
const { tooltipThemeOverrides, tooltipContentStyle } = useEditorTooltipTheme({
  maxWidth: '280px',
});

/**
 * 判断指定版本项是否正在执行模型加载。
 *
 * @param versionId 版本记录 ID。
 * @returns 该版本是当前切换目标时返回 true。
 */
function isSwitchingVersion(versionId: string): boolean {
  return props.switchingVersionId === versionId;
}

/**
 * 将初始加载目标版本滚动到列表可视区域。
 *
 * 设计说明：滚动目标由页面初始化流程明确传入，可以来自 versionId、adoptedCommitId 或
 * OpenProject 实际打开后的 currentCommitId；组件本身不猜测目标，且滚动成功后只消费一次，
 * 避免后续用户切换或记录刷新时把列表视角重新拉回初始版本。
 */
function scheduleScrollToInitialVersion(): void {
  const initialScrollVersionId = props.initialScrollVersionId?.trim() ?? '';
  if (
    props.loading
    || !initialScrollVersionId
    || consumedInitialScrollVersionId.value === initialScrollVersionId
  ) {
    return;
  }

  void nextTick(() => {
    const latestInitialScrollVersionId = props.initialScrollVersionId?.trim() ?? '';
    if (props.loading || latestInitialScrollVersionId !== initialScrollVersionId) {
      return;
    }

    const targetElement = Array.from(
      listBodyRef.value?.querySelectorAll<HTMLElement>('[data-version-id]') ?? [],
    ).find((element) => element.dataset.versionId === initialScrollVersionId);
    if (!targetElement) {
      return;
    }

    targetElement.scrollIntoView({
      block: 'center',
      inline: 'nearest',
    });
    consumedInitialScrollVersionId.value = initialScrollVersionId;
  });
}

watch(
  () => [props.initialScrollVersionId, props.records, props.loading],
  scheduleScrollToInitialVersion,
  { immediate: true },
);

type VersionPreviewListThemeVars = CSSProperties & Record<string, string>;

const isDarkTheme = computed(() => Boolean(globalConfigStore.getProviderTheme()));

const themeColorVars = computed<VersionPreviewListThemeVars>(() => {
  if (!isDarkTheme.value) {
    return {
      '--version-preview-card-bg': '#f8fafc',
      '--version-preview-card-hover-bg': '#f3f7fb',
      '--version-preview-card-selected-bg': '#eaf4ff',
      '--version-preview-card-border': '#d3deea',
      '--version-preview-card-active-border': '#72aef2',
      '--version-preview-card-title-marker': '#2d8cf0',
      '--version-preview-card-title-marker-ring': 'rgb(45 140 240 / 16%)',
      '--version-preview-card-title-marker-active': '#1677d2',
      '--version-preview-card-meta': '#64748b',
      '--version-preview-card-branch': '#1677d2',
      '--version-preview-card-shadow': '0 1px 2px rgb(15 23 42 / 7%)',
      '--version-preview-tooltip-bg': '#ffffff',
      '--version-preview-tooltip-text': '#263548',
      '--version-preview-tooltip-border': '#d6e2ef',
      '--version-preview-tooltip-shadow': '0 8px 24px rgb(15 23 42 / 16%)',
    };
  }

  return {
    '--version-preview-card-bg': '#202832',
    '--version-preview-card-hover-bg': '#26313e',
    '--version-preview-card-selected-bg': '#21364d',
    '--version-preview-card-border': '#344151',
    '--version-preview-card-active-border': '#4f91d8',
    '--version-preview-card-title-marker': '#69b8ff',
    '--version-preview-card-title-marker-ring': 'rgb(105 184 255 / 22%)',
    '--version-preview-card-title-marker-active': '#cce9ff',
    '--version-preview-card-meta': '#9aa7b5',
    '--version-preview-card-branch': '#7db9f1',
    '--version-preview-card-shadow': 'inset 0 1px 0 rgb(255 255 255 / 4%), 0 1px 2px rgb(0 0 0 / 20%)',
    '--version-preview-tooltip-bg': '#252b36',
    '--version-preview-tooltip-text': '#f3f7fb',
    '--version-preview-tooltip-border': '#3a4656',
    '--version-preview-tooltip-shadow': '0 10px 28px rgb(0 0 0 / 32%)',
  };
});

/**
 * 格式化提交时间。
 *
 * @param value ISO 时间字符串。
 * @returns 页面展示时间。
 */
function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hour}:${minute}`;
}
</script>

<style scoped>
.version-preview-list {
  --version-preview-list-bg: var(--editor-side-panel-drawer-bg, var(--n-color));
  --version-preview-list-item-bg: var(--version-preview-card-bg);
  --version-preview-list-item-hover-bg: var(--version-preview-card-hover-bg);
  --version-preview-list-item-selected-bg: var(--version-preview-card-selected-bg);
  --version-preview-list-border: var(--editor-side-panel-divider, var(--version-preview-card-border));
  --version-preview-list-active-border: var(--version-preview-card-active-border);
  --version-preview-list-title: var(--n-text-color);
  --version-preview-list-meta: var(--version-preview-card-meta);
  --version-preview-list-branch: var(--version-preview-card-branch);
  --version-preview-list-title-marker: var(--version-preview-card-title-marker);
  --version-preview-list-title-marker-ring: var(--version-preview-card-title-marker-ring);
  --version-preview-list-title-marker-active: var(--version-preview-card-title-marker-active);
  --version-preview-list-shadow: var(--version-preview-card-shadow);
  background: var(--version-preview-list-bg);
}

.version-preview-list__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--editor-side-panel-header-gap, 10px);
  padding: var(--editor-side-panel-header-padding, 14px 14px 12px);
  border-bottom: 1px solid var(--version-preview-list-border);
  background: var(--version-preview-list-bg);
}

.version-preview-list__title {
  margin: 0;
  color: var(--version-preview-list-title);
  font-size: var(--editor-side-panel-title-size, 15px);
  font-weight: var(--editor-side-panel-title-weight, 700);
  line-height: var(--editor-side-panel-title-line-height, 1.35);
}

.version-preview-list__body {
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
  padding: 12px 14px 18px;
}

.version-preview-list__empty {
  margin-top: 80px;
}

.version-preview-list__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 220px;
  height: 100%;
  color: var(--version-preview-list-meta);
}

.version-preview-list__loading-text {
  color: var(--version-preview-list-meta);
  font-size: 13px;
  line-height: 1.45;
}

.version-preview-list__item {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 11px 12px 10px 14px;
  border: 1px solid var(--version-preview-list-border);
  border-radius: 8px;
  background: var(--version-preview-list-item-bg);
  color: var(--version-preview-list-title);
  text-align: left;
  cursor: pointer;
  box-shadow: var(--version-preview-list-shadow);
  transition:
    background-color 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    transform 0.12s ease;
}

.version-preview-list__item::before {
  content: '';
  position: absolute;
  top: 12px;
  bottom: 12px;
  left: 0;
  width: 4px;
  border-radius: 0 4px 4px 0;
  background: transparent;
  transition:
    background-color 0.16s ease,
    opacity 0.16s ease;
}

.version-preview-list__item + .version-preview-list__item {
  margin-top: 9px;
}

.version-preview-list__item:hover,
.version-preview-list__item:focus-visible {
  background: var(--version-preview-list-item-hover-bg);
  border-color: var(--version-preview-list-active-border);
  box-shadow:
    var(--version-preview-list-shadow),
    inset 0 0 0 1px color-mix(in srgb, var(--n-primary-color) 18%, transparent);
}

.version-preview-list__item:hover::before,
.version-preview-list__item:focus-visible::before {
  background: var(--n-primary-color);
  opacity: 0.56;
}

.version-preview-list__item:active {
  transform: translateY(1px);
  background: var(--version-preview-list-item-selected-bg);
  border-color: var(--version-preview-list-active-border);
}

.version-preview-list__item--selected {
  background: var(--version-preview-list-item-selected-bg);
  border-color: var(--version-preview-list-active-border);
  box-shadow:
    var(--version-preview-list-shadow),
    inset 0 0 0 1px color-mix(in srgb, var(--n-primary-color) 30%, transparent);
}

.version-preview-list__item--selected::before {
  background: var(--n-primary-color);
  opacity: 1;
}

.version-preview-list__item[aria-busy='true'] {
  cursor: progress;
}

.version-preview-list__title-row {
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto auto;
  align-items: center;
  column-gap: 8px;
  min-width: 0;
}

.version-preview-list__title-marker {
  width: 7px;
  height: 7px;
  border: 1px solid color-mix(in srgb, var(--version-preview-list-title-marker) 72%, #fff 28%);
  border-radius: 999px;
  background: var(--version-preview-list-title-marker);
  box-shadow: 0 0 0 3px var(--version-preview-list-title-marker-ring);
  transition:
    background-color 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.16s ease;
}

.version-preview-list__item-title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 800;
  line-height: 1.35;
  transition: color 0.16s ease;
}

.version-preview-list__item:hover .version-preview-list__item-title,
.version-preview-list__item:focus-visible .version-preview-list__item-title,
.version-preview-list__item--selected .version-preview-list__item-title {
  color: var(--n-primary-color);
}

.version-preview-list__item:hover .version-preview-list__title-marker,
.version-preview-list__item:focus-visible .version-preview-list__title-marker,
.version-preview-list__item--selected .version-preview-list__title-marker {
  background: var(--version-preview-list-title-marker-active);
  border-color: color-mix(in srgb, var(--version-preview-list-title-marker-active) 62%, #fff 38%);
  box-shadow: 0 0 0 4px var(--version-preview-list-title-marker-ring);
}

.version-preview-list__adopted-tag {
  flex: 0 0 auto;
}

.version-preview-list__switching-spin {
  flex: 0 0 auto;
  width: 12px;
  height: 12px;
  font-size: 12px;
}

.version-preview-list__switching-spin :deep(.n-spin-body),
.version-preview-list__switching-spin :deep(.n-spin-content) {
  width: 12px;
  height: 12px;
}

.version-preview-list__branch-row {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  padding-left: 18px;
  white-space: nowrap;
  color: var(--version-preview-list-meta);
  font-size: 12px;
  line-height: 1.45;
}

.version-preview-list__branch-group {
  display: inline-flex;
  flex: 1 1 auto;
  align-items: center;
  min-width: 0;
  color: var(--version-preview-list-branch);
}

.version-preview-list__branch-icon {
  flex: 0 0 auto;
  width: 13px;
  height: 13px;
  margin-right: 4px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
  opacity: 0.86;
}

.version-preview-list__author {
  flex: 0 1 auto;
  max-width: 78px;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--version-preview-list-meta);
}

.version-preview-list__branch {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--version-preview-list-branch);
}

.version-preview-list__time {
  display: block;
  padding-left: 18px;
  color: color-mix(in srgb, var(--version-preview-list-meta) 86%, transparent);
  font-size: 12px;
  line-height: 1.45;
}
</style>
